import { useMemo } from 'react';

import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import {
  isMessagePopupType,
  isOfferPopupType,
  isOfferV2PopupType,
  isRoadPopupType,
  isRollingOfferPopupType,
} from '../../../utils/events';
import type { EventTriggerWidgetDataType, EventTriggerWidgetType, WidgetEntry } from '../types';

type EventTriggerWidgetsType = Record<EventTriggerWidgetType, EventTriggerWidgetDataType[]>;

export function useEventTriggerWidgets() {
  const { eventTriggerStore } = useStores();

  const { widgetEventsLists } = eventTriggerStore;
  const offersFF = useFlag('offers_feat', true);
  const messagesFF = useFlag('messages_feat', true);
  const roadFF = useFlag('road_feature_web', false);

  const eventTriggerWidgets: EventTriggerWidgetsType = useMemo(() => {
    let offers: EventTriggerWidgetDataType[] = [];
    if (offersFF) {
      offers = widgetEventsLists
        .filter(
          (event) =>
            isOfferPopupType(event.benefit) ||
            isOfferV2PopupType(event.benefit) ||
            isRollingOfferPopupType(event.benefit),
        )
        .map((offer) => ({
          key: offer.userEventTriggerId,
          type: 'offer',
          event: offer,
          weight: offer.eventTrigger.weight,
          dataTestId: 'offer-widget',
        }));
    }

    let messages: EventTriggerWidgetDataType[] = [];
    if (messagesFF) {
      messages = widgetEventsLists
        .filter((event) => isMessagePopupType(event.benefit) && !!event.benefit.miniImageUrl)
        .map((message) => ({
          key: message.userEventTriggerId,
          type: 'message',
          event: message,
          weight: message.eventTrigger.weight,
          dataTestId: 'message-widget',
        }));
    }

    let roads: EventTriggerWidgetDataType[] = [];
    if (roadFF) {
      roads = widgetEventsLists
        .filter((event) => isRoadPopupType(event.benefit))
        .map((road) => ({
          key: road.userEventTriggerId,
          type: 'road',
          event: road,
          weight: road.eventTrigger.weight,
          dataTestId: 'road-widget',
        }));
    }

    return {
      offer: offers,
      message: messages,
      road: roads,
    };
  }, [messagesFF, offersFF, roadFF, widgetEventsLists]);

  return useMemo(
    () =>
      Object.values(eventTriggerWidgets)
        .flat()
        .map((data): WidgetEntry => ({ data, type: data.type, id: data.key })),
    [eventTriggerWidgets],
  );
}
